import { Paper } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  addContratista,
  addEmpresa,
  addPuestoAsociado,
  addSustancia,
  addTrabajador,
  clearRes,
  postCabecera,
  postObservaciones
} from "../../../redux/actions/resolucion";
import Breadcrumb from "../../commons/breadcrumb/Breadcrumb";
import ConfirmDialog from "../../commons/dialog/ConfirmDialog";
import DataLabel from "../../commons/labels/DataLabel";
import HeaderForm from "../../commons/labels/HeaderForm";
import { StyledResolucion } from "../../styled/Prevencion";
import CabeceraContainer from "./cabecera/CabeceraContainer";

import { isNoMeasureUso } from "../../../util/resolucion";
import { normalizeString } from "../../../util/stringUtils";
import UnsavedChangesDialog from "../../commons/dialog/UnsavedChangesDialog";
import EstablecimientoContainer from "./establecimiento/EstablecimientoContainer";
import PuestosAsociadosContainer from "./puestos/PuestosAsociadosContainer";
import SustanciasContainer from "./sustancias/SustanciasContainer";
import TrabajadoresSectorContainer from "./trabajadores/TrabajadoresSectorContainer";

const ResolucionContainer = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const datosEstablecimiento = useSelector(
    (state) => state.resolucion.datosEstablecimiento
  );
  const [form, setForm] = useState("Establecimiento");
  const [fromBread, setFromBread] = useState(false);
  const [breadcrumbFields, setBreadcrumbFields] = useState([form]);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    if(!state?.idFormulario) {
      setForm("Establecimiento");
      setBreadcrumbFields(["Establecimiento"]);
      setFromBread(false);
      setOpenDialog(false);
      dispatch(clearRes());
    }
  }, [state?.idFormulario]);

  const mapFormValues = (data) => {
    if (!data?.value?.data) {
      return;
    }

    try {
      const dataPlaceholder = data.value.data;

      // Cabecera
      dispatch(postCabecera({
        cantidadTrabajadoresProduccion: dataPlaceholder.totalTrabajadoresProduccion,
        cantidadTrabajadoresEventualesProduccion: dataPlaceholder.totalTrabajadoresProduccionEventuales,
        cantidadTrabajadoresAdministracion: dataPlaceholder.totalTrabajadoresAdministracion,
        cantidadTrabajadoresEventualesAdministracion: dataPlaceholder.totalTrabajadoresAdministracionEventuales,
        sePuedeFumar: dataPlaceholder.permiteFumar,
        espacioParaFumar: dataPlaceholder.poseeEspacioFumador,
        contacto: dataPlaceholder.idContactoEmpresa,
        responsableEmpresa: dataPlaceholder.idResponsableEmpresa,
        responsableMedicina: dataPlaceholder.idResponsableEmpresaML,
        responsableSH: dataPlaceholder.idResponsableEmpresaSH,
        idEstablecimientoDeclaradoSRT: dataPlaceholder.idEstablecimientoDeclaradoSRT,
      }))
      // Observaciones:
      dispatch(postObservaciones({
        observacionesGenerales: dataPlaceholder.observacionesGenerales,
        descripcionActividadEstablecimiento: dataPlaceholder.descripcionActividad,
      }));

      // Sustancias y puestos:
      const puestosAgregados = [];
      (dataPlaceholder.sustancias || []).forEach((sustancia) => {
        let usos = sustancia.utilizaciones ? sustancia.utilizaciones.map((uso) => ({
          idPrevForm: uso.id,
          usos: uso.idUsoOrigen,
          modoEmpleo: uso.idModoEmpleo,
          nombreUso: uso.descripcionUsoOrigen,
          nombreModoEmpleo: uso.descripcionModoEmpleo,
          codigoSRT: uso.codigoSRT,
          sinMedicion: isNoMeasureUso(normalizeString(uso.descripcionUsoOrigen))
        }))
          : [];
        let estudios = sustancia.estudios ? sustancia.estudios.map((estudio) => {
          return {
            idPrevForm: estudio.id,
            idTipoEstudio: estudio.idTipoEstudio,
            numeroFrecuencia: estudio.cantidadFrecuencia,
            frecuenciaEstudio: estudio.idFrecuenciaEstudio,
            idMetodologiaEmpleada: estudio.idMetodologiaEmpleada,
            nombreTiposEstudio: estudio.descripcionTipoEstudio,
            nombreMetodologiaEmpleada: estudio.descripcionMetodologiaEmpleada,
            codigoSRT: estudio.codigoSRT,
          }
        })
          : [];

        const proveedoresCompradores = sustancia.proveedoresCompradores ? sustancia.proveedoresCompradores.map((proveedores) => {
          return {
            idPrevForm: proveedores.id,
            razonSocial: proveedores.razonSocial,
            cuit: proveedores.cuit,
            esComprador: proveedores.esComprador,
            pais: proveedores.pais,
            provincia: proveedores.provincia
              ? proveedores.provincia
                .toLowerCase()
                .replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase())
              : proveedores.provincia,
            localidad: proveedores.localidad
              ? proveedores.localidad
                .toLowerCase()
                .replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase())
              : proveedores.localidad,
            calle: proveedores.calle,
            km: proveedores.sinNro,
            numero: proveedores.nro,
            departamento: proveedores.departamento,
            piso: proveedores.piso,
            codigoPostal: proveedores.codigoPostal,
            codigoSRT: proveedores.codigoSRT,
          }
        }) : [];
        const proveedores = proveedoresCompradores.filter((item) => !item.esComprador);
        const compradores = proveedoresCompradores.filter((item) => item.esComprador);
        let equiposRadiologicos = sustancia.equiposRadiologicos ? sustancia.equiposRadiologicos.map((equipoRadiologico) => {
          const equipoRadiologicoEstablecimiento = equipoRadiologico?.equipoRadiologicoEstablecimiento || {};

          return {
            equipoRadiologicoData: {
              idPrevForm: equipoRadiologico?.id,
              id: equipoRadiologicoEstablecimiento.id,
              idEquipoRadiologicoSRT: equipoRadiologico?.idEquipoRadiologicoSRT,
              idDependenciaEmpleador: equipoRadiologicoEstablecimiento
                .idDependenciaEmpleador,
              idTipoEquipoRadiologico: equipoRadiologicoEstablecimiento
                .idTipoEquipoRadiologico,
              nombreTipoEquipoRadiologico: equipoRadiologicoEstablecimiento
                .nombreTipoEquipoRadiologico,
              idCaracteristicaEquipoRadiologico: equipoRadiologicoEstablecimiento
                .idCaracteristicaEquipoRadiologico,
              nombreCaracteristicaEquipoRadiologico: equipoRadiologicoEstablecimiento
                .nombreCaracteristicaEquipoRadiologico,
              idTipoRadiacion: equipoRadiologicoEstablecimiento
                .idTipoRadiacion,
              nombreTipoRadiacion: equipoRadiologicoEstablecimiento
                .nombreTipoRadiacion,
              idTipoFuente: equipoRadiologicoEstablecimiento
                .idTipoFuente,
              nombreTipoFuente: equipoRadiologicoEstablecimiento
                .nombreTipoFuente,
              idTipoMetodoProcesamientoImagen: equipoRadiologicoEstablecimiento
                .idTipoMetodoProcesamientoImagen,
              nombreTipoMetodoProcesamientoImagen: equipoRadiologicoEstablecimiento
                .nombreTipoMetodoProcesamientoImagen,
              marca: equipoRadiologicoEstablecimiento
                .marca,
              modelo: equipoRadiologicoEstablecimiento
                .modelo,
              actividad: equipoRadiologicoEstablecimiento
                .actividad,
              radioisotipoFuente: equipoRadiologicoEstablecimiento
                .radioisotipoFuente,
              potenciaEquipoKVP: equipoRadiologicoEstablecimiento
                .potenciaEquipoKVP,
              potenciaEquipomAp: equipoRadiologicoEstablecimiento
                .potenciaEquipomAp,
              potenciaEquipoMeV: equipoRadiologicoEstablecimiento
                .potenciaEquipoMeV,
              procesamientoImagenes: equipoRadiologicoEstablecimiento
                .procesamientoImagenes,
              observaciones: equipoRadiologicoEstablecimiento
                .observaciones,
            },
            dosimetria: Array.isArray(equipoRadiologico?.dosimetrias) ? equipoRadiologico.dosimetrias.map((dosimetria) => ({
              idPrevForm: dosimetria.id,
              tipoDosimetria: dosimetria.idTipoDosimetria,
              metodoDosimetria: dosimetria.idMetodoDosimetria,
              cantidadPersonal: dosimetria.cantidadPersonal,
              cuitResponsable: dosimetria.cuitResponsable,
              nombreMetodoDosimetria: dosimetria.nombreMetodoDosimetria,
              nombreTipoDosimetria: dosimetria.nombreTipoDosimetria,
              codigoSRT: dosimetria.codigoSRT,
            })) : [],
            respUso: Array.isArray(equipoRadiologico?.responsablesUso) ? equipoRadiologico.responsablesUso.map((responsable) => ({
              idPrevForm: responsable.id,
              cuitResponsable: responsable.cuitResponsable,
              numeroAutorizacionIndividual: responsable.numeroAutorizacionIndividual,
              numeroPermisoHabilitante: responsable.numeroPermisoHabilitante,
              matriculaProfesional: responsable.matriculaProfesional,
              fechaExpedicion: responsable.fechaExpedicion,
              fechaVencimiento: responsable.fechaVencimiento,
              codigoSRT: responsable.codigoSRT,
            })) : [],
            respInst: Array.isArray(equipoRadiologico?.responsablesInstalacion) ? equipoRadiologico.responsablesInstalacion.map((responsable) => ({
              idPrevForm: responsable.id,
              cuitResponsable: responsable.cuitResponsable,
              numeroLicenciaHabilitante: responsable.numeroLicenciaHabilitante,
              domicilioHabilitacion: responsable.domicilioHabilitacion,
              fechaExpedicion: responsable.fechaExpedicion,
              fechaVencimiento: responsable.fechaVencimiento,
              codigoSRT: responsable.codigoSRT,
            })) : [],
            protRad: Array.isArray(equipoRadiologico?.encargadosProteccion) ? equipoRadiologico.encargadosProteccion.map((responsable) => ({
              idPrevForm: responsable.id,
              cuitResponsable: responsable.cuitResponsable,
              matriculaProfesional: responsable.matriculaProfesional,
              tituloProfesional: responsable.tituloProfesional,
              numeroHabilitacion: responsable.numeroHabilitacion,
              codigoSRT: responsable.codigoSRT,
            })) : [],
          }

        }) : [];

        let objetoSustancia = {
          sustancia: {
            cantidad: sustancia.cantidad != null ? sustancia.cantidad.toString().replace(".", ",") : "",
            nombre: sustancia.nombreComercial,
            unidad: sustancia.idUnidadMedida,
            sustancia: sustancia.idAgenteCancerigeno,
            nombreAgente: sustancia.codigoAgenteCancerigeno,
            descripcionAgente: sustancia.descripcionAgenteCancerigeno,
          },
          idPrevForm: sustancia.id,
          id: sustancia.id,
          codigoSRT: sustancia.codigoSRT,
          equiposRadiologicos,
          proveedores,
          compradores,
          estudios,
          usos,
        };

        dispatch(addSustancia(objetoSustancia));

        // Aggregate puestos: group by puesto ID, collect sustancia IDs into array
        sustancia.puestos?.forEach((p) => {
          const puestoId = p.idPuestoEstablecimiento;
          // Check if this puesto already exists (from a previous sustancia)
          const existingIndex = puestosAgregados.findIndex(
            (existing) => existing.puesto?.toString() === puestoId?.toString()
          );

          if (existingIndex >= 0) {
            // Puesto already exists, just add this sustancia ID
            if (!puestosAgregados[existingIndex].sustancias.includes(sustancia.id)) {
              puestosAgregados[existingIndex].sustancias.push(sustancia.id);
            }
          } else {
            // New puesto record
            puestosAgregados.push({
              idPrevForm: p.id,
              sustancias: [sustancia.id],
              nombrePuesto: p.nombrePuestoEstablecimiento,
              puesto: p.idPuestoEstablecimiento,
              descripcionActividades: p.descripcion,
              descripcionEstudios: p.descripcionEstudios,
              informaRiesgos: p.informaSobreRiesgos,
              poseeCapacitaciones: p.capacitacionSobreRiesgos,
              entregaProteccion: p.entregaElementosDeProteccion,
              licenciasEspeciales: p.licenciaEspecial,
              elementosDeProteccion:
                p.elementosProteccionPersonal ? p.elementosProteccionPersonal.map((elementoDeProteccion) => ({
                  idPrevForm: elementoDeProteccion.id,
                  idElementoDeProteccion: elementoDeProteccion.idElementoProteccionPersonal,
                  ...(elementoDeProteccion.codigoSRT && {
                    codigoSRT: elementoDeProteccion.codigoSRT,
                  }),
                })) : [],
              medidasPreventivas:
                p.medidasPreventivas ? p.medidasPreventivas?.map((medidaPreventiva) => ({
                  idPrevForm: medidaPreventiva.id,
                  idMedidaPreventiva: medidaPreventiva.idMedidaPreventivaCancerigenos,
                  ...(medidaPreventiva.codigoSRT && {
                    codigoSRT: medidaPreventiva.codigoSRT,
                  }),
                })) : [],
              codigoSRT: p.codigoSRT,
            });
          }
        });

      })
      // Dispatch all aggregated puestos
      puestosAgregados.forEach((puesto) => {
        dispatch(addPuestoAsociado(puesto));
      });

      // Trabajadores:
      (dataPlaceholder.trabajadores || []).forEach((trabajador) => {
        let nuevoTrabajador = {
          idPrevForm: trabajador.id,
          cuil: trabajador.cuitNomina,
          nombreApellidoTrabajador: trabajador.nombreCompletoNomina,
          nombreSector: trabajador.nombreSectorEstablecimiento,
          nombrePuesto: trabajador.nombrePuestoEstablecimiento,
          idPuesto: trabajador.idPuestoEstablecimiento,
          idSector: trabajador.idSectorEstablecimiento,
          fechaIngreso: trabajador.fechaIngresoEmpresa,
          inicioContacto: trabajador.fechaInicioContacto,
          finContacto: trabajador.fechaFinContacto,
          exposicion: trabajador.sinExposicion,
          sustanciasAsociadasAPuesto: [],
          examenes: {},
          idTrabajador: trabajador.idNomina,
          permanente: trabajador.permanente,
          codigoSRT: trabajador.codigoSRT,
          codigoActividadSRT: trabajador.codigoActividadSRT,
        }
        // Asignar examenes y fechas por sustancia
        trabajador.sustancias?.forEach((workerSub) => {
          const subId = workerSub.idSustancia;
          if (!nuevoTrabajador.examenes[subId]) {
            nuevoTrabajador.examenes[subId] = {};
          }

          // Mapear fechas de exposición de la sustancia
          nuevoTrabajador.examenes[subId].inicioContacto = workerSub.fechaInicioContacto;
          nuevoTrabajador.examenes[subId].finContacto = workerSub.fechaFinContacto;

          // Mapear exámenes médicos de la sustancia
          workerSub.examenesMedicos?.forEach((examen) => {
            nuevoTrabajador.examenes[subId][examen.idTipoExamenMedico] = {
              idPrevForm: examen.id,
              value: true,
              idExamenDeclaradoSRT: examen.idExamenDeclaradoSRT
            };
          });
        });
        dispatch(addTrabajador(nuevoTrabajador))
      });

      // Contratistas
      (dataPlaceholder.contratistas || []).forEach((contratista, index) => {
        dispatch(addContratista({
          idPrevForm: contratista.id,
          id: index,
          cuit: contratista.cuit,
          ciiu: contratista.actividad,
          cantidadEmpleados: contratista.cantidadTrabajadores,
          idContratistaSRT: contratista.idContratistaSRT,
        }))
      });

      // Empresas
      (dataPlaceholder.empresasTercerizadas || []).forEach((contratista, index) => {
        dispatch(addEmpresa({
          idPrevForm: contratista.id,
          id: index,
          cuit: contratista.cuit,
          ciiu: contratista.actividad,
          cantidadEmpleados: contratista.cantidadTrabajadores,
          idEmpresaSRT: contratista.idEmpresaSRT,
        }))
      })
    } catch (error) {
      console.error('Error al mapear datos del formulario:', error);

      // Limpiar el estado de Redux para evitar carga parcial de datos
      dispatch(clearRes());

      // Notificar al usuario
      toast.error(
        'Ocurrió un error al cargar los datos del formulario. Por favor, intente nuevamente.',
        { autoClose: 5000 }
      );

      // Re-lanzar el error para que el componente padre pueda manejarlo si es necesario
      throw error;
    }
  }

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleConfirmCancel = () => {
    if (!state?.idDependenciaEmpleador) {
      dispatch(clearRes());
      setForm("Establecimiento");
      setBreadcrumbFields(["Establecimiento"]);
      setOpenDialog(false);
    } else {
      dispatch(clearRes());
      setOpenDialog(false);
      navigate(`/prevencion/establecimientos/${state?.idDependenciaEmpleador}`, {
        state: {
          ...datosEstablecimiento.data,
          idDependenciaEmpleador: state?.idDependenciaEmpleador,
          skipUnsavedGuard: true,
        },
      });
    }

  };

  const handleSetForm = (form) => {
    if (!breadcrumbFields.includes(form)) {
      const auxArray = [...breadcrumbFields, form];
      setBreadcrumbFields(auxArray);
    }
    setForm(form);
  };

  return (
    <>
      <HeaderForm title={"Formulario Res. 81/19"} />
      <Paper style={{ overflowX: "hidden", paddingLeft: "20px", paddingRight: "20px" }}>
        <StyledResolucion.BreadcrumbContainer>
          <Breadcrumb
            setForm={setForm}
            setFields={setBreadcrumbFields}
            fields={breadcrumbFields}
            setFromBread={() => setFromBread(true)}
            form={form}
          />
        </StyledResolucion.BreadcrumbContainer>
        {form !== "Establecimiento" && (
          <StyledResolucion.FormsContainer>
            <DataLabel
              firstText={"MU: " + datosEstablecimiento.data?.matrizUnica}
              secondText={datosEstablecimiento.data?.nombre?.split("-")[0]}
              thirdText={datosEstablecimiento.data?.domicilio}
            />
          </StyledResolucion.FormsContainer>
        )}
        {
          {
            Establecimiento: (
              <EstablecimientoContainer
                nextForm={handleSetForm}
                fromBread={fromBread}
                mapFormValues={mapFormValues}
                handleCancelResolucion={handleOpenDialog}
              />
            ),
            Cabecera: (
              <CabeceraContainer
                nextForm={handleSetForm}
                handleCancelResolucion={handleOpenDialog}
                establecimiento={datosEstablecimiento.data}
              />
            ),
            Sustancias: (
              <SustanciasContainer
                nextForm={handleSetForm}
                handleCancelResolucion={handleOpenDialog}
                establecimiento={datosEstablecimiento.data}
              />
            ),
            PuestosAsociados: (
              <PuestosAsociadosContainer
                handleCancelResolucion={handleOpenDialog}
                nextForm={handleSetForm}
                establecimiento={datosEstablecimiento.data}
              />
            ),
            TrabajadoresSector: (
              <TrabajadoresSectorContainer
                nextForm={handleSetForm}
                handleCancelResolucion={handleOpenDialog}
              />
            ),

          }[form]
        }
      </Paper>
      <UnsavedChangesDialog
        shouldBlockNavigation={Object.keys(datosEstablecimiento.data).length !== 0}
        title="Cambios sin guardar"
        text="¿Desea abandonar la página? Se perderán los cambios realizados en el formulario"
      />
      <ConfirmDialog
        open={openDialog}
        handleClose={handleCloseDialog}
        handleConfirm={handleConfirmCancel}
        loading={false}
        title="Cancelar"
        text="¿Está seguro de que desea cancelar? Toda la carga realizada en el formulario no se guardará"
      />
    </>
  );
};

export default ResolucionContainer;
